/**
 * Member Dashboard - Olympic Fit Wellness
 * Connected to live Supabase APIs
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Activity, 
  Flame,
  Clock,
  TrendingUp,
  ChevronRight,
  Dumbbell,
  Star,
  Award
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';

export default function MemberDashboard() {
  const { profile, user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [membership, setMembership] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    workoutsThisMonth: 0,
    classesBooked: 0,
    currentStreak: 0
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // Load bookings from API
      const bookingsData = await api.getMyBookings();
      const confirmedBookings = (bookingsData || []).filter(b => b.status === 'confirmed');
      setBookings(confirmedBookings.slice(0, 3));
      
      // Calculate stats from bookings
      const now = new Date();
      const thisMonth = now.getMonth();
      const thisYear = now.getFullYear();
      const completedThisMonth = (bookingsData || []).filter(b => {
        if (b.status !== 'confirmed') return false;
        const bookDate = new Date(b.class_instances?.start_time || b.booked_at);
        return bookDate.getMonth() === thisMonth && bookDate.getFullYear() === thisYear;
      });
      
      setStats({
        workoutsThisMonth: completedThisMonth.length,
        classesBooked: confirmedBookings.length,
        currentStreak: Math.min(completedThisMonth.length, 7)
      });

      // Load membership from API
      const membershipData = await api.getMyMembership();
      setMembership(membershipData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Use fallback data on error
      setBookings([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getTierColor = (tier) => {
    switch(tier?.toLowerCase()) {
      case 'elite': return 'from-yellow-600 to-amber-700';
      case 'wellness': return 'from-blue-600 to-indigo-700';
      default: return 'from-neutral-600 to-neutral-700';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="space-y-8" data-testid="member-dashboard">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-[#3B82F6] to-blue-700 rounded-xl p-8 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold mb-2">
              Welcome back, {profile?.full_name?.split(' ')[0] || 'Member'}!
            </h1>
            <p className="text-blue-100">
              {stats.workoutsThisMonth > 0 
                ? `You've completed ${stats.workoutsThisMonth} workouts this month. Keep going!`
                : 'Ready to start your fitness journey? Book a class today!'}
            </p>
          </div>
          <Link
            to="/portal/classes"
            className="inline-flex items-center gap-2 bg-white text-[#3B82F6] font-semibold px-6 py-3 text-sm tracking-wider hover:bg-blue-50 transition-colors"
            data-testid="book-class-cta"
          >
            BOOK A CLASS <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Workouts This Month', value: stats.workoutsThisMonth, icon: Dumbbell, color: 'bg-purple-100 text-purple-600' },
          { label: 'Classes Booked', value: stats.classesBooked, icon: Calendar, color: 'bg-blue-100 text-blue-600' },
          { label: 'Current Streak', value: `${stats.currentStreak} days`, icon: Flame, color: 'bg-orange-100 text-orange-600' },
          { label: 'Member Since', value: profile?.created_at ? new Date(profile.created_at).getFullYear() : 'N/A', icon: Award, color: 'bg-green-100 text-green-600' }
        ].map((stat, idx) => (
          <div key={idx} className="bg-white rounded-xl shadow-sm p-4">
            <div className={`w-10 h-10 rounded-lg ${stat.color.split(' ')[0]} flex items-center justify-center mb-3`}>
              <stat.icon className={`w-5 h-5 ${stat.color.split(' ')[1]}`} />
            </div>
            <div className="text-2xl font-bold text-neutral-900">{stat.value}</div>
            <div className="text-sm text-neutral-500">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Upcoming Classes */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm">
          <div className="flex items-center justify-between p-6 border-b border-neutral-100">
            <h2 className="text-xl font-heading font-bold text-neutral-900">Upcoming Classes</h2>
            <Link to="/portal/schedule" className="text-[#3B82F6] text-sm font-medium hover:underline flex items-center gap-1">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="w-10 h-10 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : bookings.length > 0 ? (
            <div className="divide-y divide-neutral-100">
              {bookings.map((booking) => (
                <div key={booking.booking_id} className="p-4 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center">
                    <Calendar className="w-7 h-7 text-[#3B82F6]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-neutral-900">
                      {booking.class_instances?.classes?.name || 'Class'}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-neutral-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(booking.class_instances?.start_time)} at {formatTime(booking.class_instances?.start_time)}
                      </span>
                    </div>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    Confirmed
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
              <h3 className="font-semibold text-neutral-900 mb-2">No Upcoming Classes</h3>
              <p className="text-neutral-500 mb-6">Book a class to get started!</p>
              <Link
                to="/portal/classes"
                className="inline-flex items-center gap-2 bg-[#3B82F6] text-white font-semibold px-6 py-3 text-sm rounded-lg hover:bg-blue-600 transition-colors"
              >
                Browse Classes
              </Link>
            </div>
          )}
        </div>

        {/* Membership Card */}
        <div className="space-y-6">
          <div className={`bg-gradient-to-br ${getTierColor(profile?.membership_tier)} rounded-xl p-6 text-white`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5" />
                <span className="font-medium tracking-wider text-sm">
                  {(profile?.membership_tier || 'MEMBER').toUpperCase()}
                </span>
              </div>
              <span className="text-xs opacity-75">OFW</span>
            </div>
            <div className="mb-6">
              <p className="text-sm opacity-75">Member</p>
              <p className="text-xl font-bold">{profile?.full_name || 'Member'}</p>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>{user?.email}</span>
              <span className="opacity-75">Valid</span>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-neutral-900 mb-4">Quick Actions</h3>
            <div className="space-y-2">
              {[
                { label: 'Book a Class', href: '/portal/classes', icon: Calendar },
                { label: 'My Schedule', href: '/portal/schedule', icon: Clock },
                { label: 'My Workouts', href: '/portal/workouts', icon: Dumbbell },
                { label: 'Membership', href: '/portal/membership', icon: Star }
              ].map((action, idx) => (
                <Link
                  key={idx}
                  to={action.href}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-neutral-50 transition-colors group"
                >
                  <action.icon className="w-5 h-5 text-neutral-400 group-hover:text-[#3B82F6]" />
                  <span className="text-neutral-700 group-hover:text-neutral-900">{action.label}</span>
                  <ChevronRight className="w-4 h-4 text-neutral-400 ml-auto" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
